import { applyListHtml, detectListKind } from './listHtml';
import { TEXT_STYLE_STEPS, textStylePatch } from './layerList';
import { DEFAULT_TEXT_PADDING } from './shapeFrame';
import { presentationEditorHtml, stripPresentationText } from './textSanitize';
import {
  SLIDE_HEIGHT,
  SLIDE_WIDTH,
  SlideObject,
  createObjectId,
  defaultLocks,
} from '../types';

export const PRESENTATION_FIELD_TEXT_DRAG = 'application/x-athena-presentation-field-text';

export type LibraryFieldTextKind = 'heading' | 'body' | 'bullets';

export type LibraryFieldTransfer = {
  label: string;
  text: string;
  html: string;
  kind?: LibraryFieldTextKind;
};

export type LibraryFieldTransferInput = {
  label?: string;
  text: string;
  html?: string;
  kind?: LibraryFieldTextKind;
};

const EMPTY_PLACEHOLDER = '—';

export function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function htmlFromPlain(text: string): string {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  if (!lines.some((line) => line.trim())) return '<p></p>';
  return lines.map((line) => `<p>${escapeHtml(line)}</p>`).join('');
}

export function htmlFromBullets(items: string[]): string {
  const lines = items.map((item) => String(item || '').trim()).filter(Boolean);
  if (!lines.length) return '<p></p>';
  return `<ul>${lines.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
}

export function inferFieldTextStyle(payload: LibraryFieldTransferInput): LibraryFieldTextKind {
  if (payload.kind === 'heading' || payload.kind === 'body' || payload.kind === 'bullets') {
    return payload.kind;
  }
  if (detectListKind(payload.html) === 'bullet') return 'bullets';
  return 'body';
}

export function toLibraryFieldTransfer(payload: LibraryFieldTransferInput): LibraryFieldTransfer | null {
  const text = String(payload.text || '').trim();
  if (!text || text === EMPTY_PLACEHOLDER) return null;
  const kind = inferFieldTextStyle(payload);
  const html = payload.html?.trim()
    || (kind === 'bullets'
      ? htmlFromBullets(text.split(/\r?\n/).map((item) => item.trim()).filter(Boolean))
      : htmlFromPlain(text));
  return {
    label: String(payload.label || '').trim(),
    text,
    html,
    kind,
  };
}

export function writeLibraryFieldTransfer(dataTransfer: DataTransfer, payload: LibraryFieldTransfer): void {
  dataTransfer.setData('text/plain', payload.text);
  dataTransfer.setData('text/html', payload.html);
  dataTransfer.setData(PRESENTATION_FIELD_TEXT_DRAG, JSON.stringify(payload));
  dataTransfer.effectAllowed = 'copy';
}

export function hasLibraryFieldTransfer(types: readonly string[]): boolean {
  return types.includes(PRESENTATION_FIELD_TEXT_DRAG)
    || types.includes('text/plain')
    || types.includes('text/html')
    || types.includes('Text');
}

export function readLibraryFieldTransfer(dataTransfer: DataTransfer): LibraryFieldTransfer | null {
  const raw = dataTransfer.getData(PRESENTATION_FIELD_TEXT_DRAG);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as LibraryFieldTransferInput;
      const transfer = toLibraryFieldTransfer(parsed);
      if (transfer) return transfer;
    } catch {
      // Fall through to plain text.
    }
  }
  const text = dataTransfer.getData('text/plain');
  return toLibraryFieldTransfer({
    text,
    html: dataTransfer.getData('text/html') || undefined,
  });
}

export function estimateLibraryTextSize(html: string, kind: LibraryFieldTextKind): { w: number; h: number } {
  const text = String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const blocks = Math.max(
    1,
    (String(html || '').match(/<p\b|<li\b|<br\b/gi) || []).length,
    Math.ceil(Math.max(1, text.length) / 42),
  );
  if (kind === 'heading') {
    return {
      w: Math.min(1600, Math.max(420, Math.round(text.length * 28))),
      h: Math.min(280, Math.max(120, Math.round(48 * 1.05 * Math.ceil(text.length / 28) + 24))),
    };
  }
  const lineHeight = kind === 'bullets' ? 40 : 36;
  return {
    w: text.length > 48 || blocks > 1 ? 820 : Math.min(820, Math.max(360, Math.round(text.length * 18))),
    h: Math.min(720, Math.max(80, blocks * lineHeight + 28)),
  };
}

export function createLibraryTextObject(
  payload: LibraryFieldTransfer,
  position: { x: number; y: number; z: number },
  bounds?: { width: number; height: number },
): SlideObject {
  const kind = inferFieldTextStyle(payload);
  const step = kind === 'heading'
    ? TEXT_STYLE_STEPS.find((item) => item.id === 'subheading')
    : kind === 'bullets'
      ? TEXT_STYLE_STEPS.find((item) => item.id === 'bullets')
      : TEXT_STYLE_STEPS.find((item) => item.id === 'body');
  if (!step) throw new Error('Missing text style step');
  let content = payload.html || htmlFromPlain(payload.text);
  if (kind === 'bullets') content = applyListHtml(content, 'bullet');
  content = presentationEditorHtml(content) || htmlFromPlain(payload.text);
  if (!stripPresentationText(content)) content = htmlFromPlain(payload.text);
  const slideW = bounds?.width ?? SLIDE_WIDTH;
  const slideH = bounds?.height ?? SLIDE_HEIGHT;
  const estimated = estimateLibraryTextSize(content, kind);
  const size = {
    w: Math.min(estimated.w, Math.max(80, slideW - 80)),
    h: Math.min(estimated.h, Math.max(80, slideH - 80)),
  };
  return {
    id: createObjectId('text'),
    type: 'text',
    name: payload.label || undefined,
    x: Math.max(40, Math.min(slideW - size.w - 40, position.x - size.w / 2)),
    y: Math.max(40, Math.min(slideH - size.h - 40, position.y - size.h / 2)),
    w: size.w,
    h: size.h,
    z: position.z,
    locks: defaultLocks(),
    content,
    trackingMode: 'pin',
    style: {
      ...textStylePatch(step),
      color: '#1B3967',
      padding: DEFAULT_TEXT_PADDING,
    },
  };
}
